import { createApp } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import App from './App.vue';
import './assets/tailwind.css';

// Import Supabase and logger
import { supabase } from './services/supabase';
import { logger, LogCategory } from './services/logger';
import {BusinessRole} from "@/types/businessRole";

// Extend Window interface for TypeScript
declare global {
  interface Window {
    supabase: any;
    kendo: any;
  }
}

// Create the Vue app
const app = createApp(App);

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: App,
      meta: {
        navItem: 'chatbot'
      }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: App,
      meta: {
        navItem: 'dashboard'
      }
    },
    {
      path: '/email-generator',
      name: 'email-generator',
      component: App,
      meta: {
        navItem: 'email-generator'
      }
    },
    {
      path: '/forecasting-planners',
      name: 'forecasting-planners',
      component: App,
      meta: {
        navItem: 'forecasting-planners'
      }
    },
    {
      path: '/forecasting-pco',
      name: 'forecasting-pco',
      component: App,
      meta: {
        navItem: 'forecasting-pco',
        pcoTab: 'dashboard'
      }
    },
    {
      path: '/forecasting-pco/dashboard',
      name: 'forecasting-pco-dashboard',
      component: App,
      meta: {
        navItem: 'forecasting-pco',
        pcoTab: 'dashboard'
      }
    },
    {
      path: '/forecasting-pco/calendar',
      name: 'forecasting-pco-calendar',
      component: App,
      meta: {
        navItem: 'forecasting-pco',
        pcoTab: 'calendar'
      }
    },
    {
      path: '/forecasting-pco/documents',
      name: 'forecasting-pco-documents',
      component: App,
      meta: {
        navItem: 'forecasting-pco',
        pcoTab: 'documents'
      }
    },
    {
      path: '/forecasting-pco/presentation',
      name: 'forecasting-pco-presentation',
      component: App,
      meta: {
        navItem: 'forecasting-pco',
        pcoTab: 'presentation'
      }
    },
    {
      path: '/chatbot',
      name: 'chatbot',
      component: App,
      meta: {
        navItem: 'chatbot'
      }
    },
    {
      path: '/administration',
      name: 'administration',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: ''
      }
    },
    {
      path: '/administration/email-templates',
      name: 'email-templates',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'email-templates'
      }
    },
    {
      path: '/administration/user-roles',
      name: 'user-roles',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'user-roles'
      }
    },
    {
      path: '/administration/system-config',
      name: 'system-config',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'system-config'
      }
    },
    {
      path: '/administration/audit-logs',
      name: 'audit-logs',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'audit-logs'
      }
    },
    {
      path: '/administration/manage-knowledgebase',
      name: 'manage-knowledgebase',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'manage-knowledgebase'
      }
    },
    {
      path: '/administration/due-dates',
      name: 'due-dates',
      component: App,
      meta: {
        navItem: 'administration',
        adminSubView: 'due-dates'
      }
    },
    {
      path: '/supabase-test',
      name: 'supabase-test',
      component: App,
      meta: {
        navItem: 'supabase-test'
      }
    },
    {
      path: '/storage-test',
      name: 'storage-test',
      component: App,
      meta: {
        navItem: 'storage-test'
      }
    },
    {
      path: '/user-settings',
      name: 'user-settings',
      component: App,
      meta: {
        navItem: 'user-settings'
      }
    },
    {
      path: '/template-storage',
      name: 'template-storage',
      component: App,
      meta: {
        navItem: 'template-storage'
      }
    },
    {
      path: '/knowledgebase-upload',
      name: 'knowledgebase-upload',
      component: App,
      meta: {
        navItem: 'knowledgebase-upload'
      }
    },
    // Catch-all route for 404
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ],
});

// Add router to the app
app.use(router);

// Add navigation guard for role-based access control
router.beforeEach(async (to, from, next) => {
  // Check if the route is a forecasting-pco route
  if (to.path.startsWith('/forecasting-pco')) {
    try {
      // Get the current session
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) {
        // If no session, redirect to home
        logger.warn('Unauthorized access attempt to PCO route - No session', LogCategory.AUTH, {
          path: to.path
        });
        return next('/');
      }
      
      // Get the user's profile to check business role
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('business_role')
        .eq('id', data.session.user.id)
        .single();
      
      if (profileError || !profileData) {
        logger.error('Error fetching user profile for role check', LogCategory.AUTH, {
          error: profileError,
          userId: data.session.user.id
        });
        return next('/chatbot');
      }
      
      // Check if user has the required role (HEAD_OF_PCO or CONTROLLER)
      const { business_role } = profileData;
      
      if (business_role === BusinessRole.HEAD_OF_PCO || business_role === BusinessRole.CONTROLLER) {
        // User has permission, allow access
        return next();
      } else {
        // User doesn't have permission, redirect to dashboard
        logger.warn('Unauthorized access attempt to PCO route - Insufficient role', LogCategory.AUTH, {
          path: to.path,
          userRole: business_role
        });
        return next('/chatbot');
      }
    } catch (error) {
      logger.error('Error in navigation guard', LogCategory.AUTH, { error });
      return next('/chatbot');
    }
  }
  
  // Check if the route is a forecasting-planners route
  if (to.path.startsWith('/forecasting-planners')) {
    try {
      // Get the current session
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) {
        // If no session, redirect to home
        logger.warn('Unauthorized access attempt to Planners route - No session', LogCategory.AUTH, {
          path: to.path
        });
        return next('/');
      }
      
      // Get the user's profile to check business role
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('business_role')
        .eq('id', data.session.user.id)
        .single();
      
      if (profileError || !profileData) {
        logger.error('Error fetching user profile for role check', LogCategory.AUTH, {
          error: profileError,
          userId: data.session.user.id
        });
        return next('/chatbot');
      }
      
      // Check if user has the required role (PLANNER)
      const { business_role } = profileData;
      
      if (business_role === BusinessRole.PLANNER) {
        // User has permission, allow access
        return next();
      } else {
        // User doesn't have permission, redirect to chatbot
        logger.warn('Unauthorized access attempt to Planners route - Insufficient role', LogCategory.AUTH, {
          path: to.path,
          userRole: business_role
        });
        return next('/chatbot');
      }
    } catch (error) {
      logger.error('Error in navigation guard', LogCategory.AUTH, { error });
      return next('/chatbot');
    }
  }
  
  // For all other routes, proceed normally
  next();
});

// Initialize auth state listener
supabase.auth.onAuthStateChange((event, session) => {
  logger.info('Auth state changed', LogCategory.AUTH, { event, userId: session?.user?.id });
});

// Check for existing session on app load
const initializeAuth = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    logger.info('Session data on app load', LogCategory.AUTH, {
      hasSession: !!data.session,
      userId: data.session?.user?.id
    });

    // Here you might want to set the user in a store or reactive variable
    // For example, if using Pinia or Vuex
  } catch (error) {
    logger.error('Failed to get auth session', LogCategory.AUTH, { error });
  }
};

// Initialize auth before mounting
initializeAuth().then(() => {
  // Make Supabase available globally for debugging in development only
  if (process.env.NODE_ENV !== 'production') {
    window.supabase = supabase;
  }

  // Mount the app
  app.mount('#app');

  // Log Supabase initialization
  logger.info('Supabase client initialized', LogCategory.GENERAL);
});
